import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Dialog,
  Modal as AriaModal,
  ModalOverlay,
  Heading,
} from 'react-aria-components'
import type { AnimationState } from '../types'
import { generateCode } from '../utils/codeGenerators'
import { SyntaxHighlightedCode } from '../utils/syntaxHighlight'
import { buildShareUrl } from '../utils/shareUrl'
import { SegmentedControl } from './controls/SegmentedControl'

type Framework = 'react' | 'vue' | 'vanilla'

const FRAMEWORK_OPTIONS: { label: string; value: Framework }[] = [
  { label: 'React', value: 'react' },
  { label: 'Vue', value: 'vue' },
  { label: 'Vanilla', value: 'vanilla' },
]

interface CodePanelProps {
  state: AnimationState
}

export function CodePanel({ state }: CodePanelProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [framework, setFramework] = useState<Framework>('react')
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null)

  const generatedCode = useMemo(
    () => generateCode(state, framework),
    [state.transform, state.transition, state.targetShape, framework]
  )

  useEffect(() => {
    if (!copyFeedback) return
    const timer = setTimeout(() => setCopyFeedback(null), 2000)
    return () => clearTimeout(timer)
  }, [copyFeedback])

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(generatedCode)
    setCopyFeedback('Copied!')
  }, [generatedCode])

  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(buildShareUrl(state))
    setCopyFeedback('Link copied!')
  }, [state])

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-2 bg-gray-900 hover:bg-gray-800 text-gray-200 text-sm font-medium rounded-lg shadow-lg transition-colors cursor-pointer"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
        Code
      </button>

      {/* Modal */}
      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        isDismissable
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm data-[entering]:animate-fade-in data-[exiting]:animate-fade-out"
      >
        <AriaModal
          className="w-full max-w-3xl mx-4 bg-gray-900 rounded-xl shadow-2xl outline-none data-[entering]:animate-zoom-in data-[exiting]:animate-zoom-out overflow-hidden"
        >
          <Dialog className="outline-none">
            {({ close }) => (
              <>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700/60">
                  <div className="flex items-center gap-4">
                    <Heading slot="title" className="text-sm font-semibold text-gray-200">
                      Generated Code
                    </Heading>
                    <div onClick={(e) => e.stopPropagation()}>
                      <SegmentedControl options={FRAMEWORK_OPTIONS} value={framework} onChange={setFramework} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {copyFeedback ? (
                      <span className="text-xs text-green-400 font-medium px-2">{copyFeedback}</span>
                    ) : (
                      <>
                        <button
                          onClick={handleCopy}
                          className="text-xs text-gray-400 hover:text-gray-200 px-2.5 py-1.5 rounded-md hover:bg-gray-700/60 transition-colors cursor-pointer"
                        >
                          Copy
                        </button>
                        <button
                          onClick={handleShare}
                          className="text-xs text-gray-400 hover:text-gray-200 px-2.5 py-1.5 rounded-md hover:bg-gray-700/60 transition-colors cursor-pointer"
                        >
                          Share
                        </button>
                      </>
                    )}
                    <button
                      onClick={close}
                      className="p-1.5 text-gray-500 hover:text-gray-300 transition-colors rounded-md hover:bg-gray-700/60 cursor-pointer ml-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Code area */}
                <div className="overflow-auto p-5" style={{ maxHeight: 'calc(80vh - 56px)' }}>
                  <SyntaxHighlightedCode code={generatedCode} />
                </div>
              </>
            )}
          </Dialog>
        </AriaModal>
      </ModalOverlay>
    </>
  )
}
